export const SEVERITY_RANK = { disruption: 2, degraded: 1 };

/**
 * Build a lookup map of "product_type::city" -> worst severity
 * from a list of incidents. Only "investigating" incidents are considered.
 */
export const buildStatusesByProductCity = (incidents) => {
    const tmpMap = {};

    incidents?.forEach(incident => {
        if (incident?.status !== 'investigating') {
            return;
        }

        const tmpStatus = incident?.severity?.toLowerCase();
        if (!tmpStatus) return;

        const rawProductType = incident?.product_type;
        const productTypes = Array.isArray(rawProductType)
            ? rawProductType
            : rawProductType ? [rawProductType] : [];
        const cities = [
            ...(incident?.planes?.control?.regions?.map(r => r.city) || []),
            ...(incident?.planes?.data?.regions?.map(r => r.city) || []),
        ];

        if (!cities.length) return;

        cities.forEach(city => {
            if (!city) return;

            if (productTypes.length) {
                productTypes.forEach(product => {
                    const key = `${product}::${city}`;
                    const existing = tmpMap[key];
                    if (!existing || (SEVERITY_RANK[tmpStatus] || 0) > (SEVERITY_RANK[existing] || 0)) {
                        tmpMap[key] = tmpStatus;
                    }
                });
            } else {
                const key = `::${city}`;
                const existing = tmpMap[key];
                if (!existing || (SEVERITY_RANK[tmpStatus] || 0) > (SEVERITY_RANK[existing] || 0)) {
                    tmpMap[key] = tmpStatus;
                }
            }
        });
    });

    return tmpMap;
};

/**
 * Build a lookup map of "product_type::city" -> Array<{ custom_incident_id, title, severity }>
 * from a list of incidents. Only "investigating" incidents are considered.
 */
export const buildIncidentsByProductCity = (incidents) => {
    const map = {};

    incidents?.forEach(incident => {
        if (incident?.status !== 'investigating') return;
        const severity = incident?.severity?.toLowerCase();
        if (!severity) return;
        const id = incident?.custom_incident_id;
        const title = incident?.title;
        if (!id) return;

        const rawProductType = incident?.product_type;
        const productTypes = Array.isArray(rawProductType)
            ? rawProductType
            : rawProductType ? [rawProductType] : [];
        const cities = [
            ...(incident?.planes?.control?.regions?.map(r => r.city) || []),
            ...(incident?.planes?.data?.regions?.map(r => r.city) || []),
        ];

        if (!cities.length) return;

        cities.forEach(city => {
            if (!city) return;
            const keys = productTypes.length
                ? productTypes.map(p => `${p}::${city}`)
                : [`::${city}`];

            keys.forEach(key => {
                if (!map[key]) map[key] = [];
                if (!map[key].some(i => i.custom_incident_id === id)) {
                    map[key].push({ custom_incident_id: id, title, severity });
                }
            });
        });
    });

    return map;
};

/**
 * Look up incidents for a single product at a specific city.
 */
export const resolveProductIncidents = (incidentsByProductCity, productName, city) => {
    if (!incidentsByProductCity || !city) return [];
    if (productName && productName !== 'none') {
        return incidentsByProductCity[`${productName}::${city}`] || [];
    }
    return incidentsByProductCity[`::${city}`] || [];
};

/**
 * Resolve status for a single product at a specific city.
 */
export const resolveProductStatus = (statusesByProductCity, productName, city) => {
    if (!statusesByProductCity || !city) return 'available';
    if (productName && productName !== 'none') {
        return statusesByProductCity[`${productName}::${city}`] || 'available';
    }
    return statusesByProductCity[`::${city}`] || 'available';
};

/**
 * Resolve the worst status for a region given its product_mapping and city,
 * using the product-city status lookup map.
 */
export const resolveRegionStatus = (statusesByProductCity, productMapping, city, selectedProducts) => {
    let regionStatus = 'available';

    const products = selectedProducts?.length
        ? productMapping?.filter(p => selectedProducts.includes(p))
        : productMapping;

    if (products?.length) {
        for (const product of products) {
            const s = statusesByProductCity?.[`${product}::${city}`];
            if (s && (SEVERITY_RANK[s] || 0) > (SEVERITY_RANK[regionStatus] || 0)) {
                regionStatus = s;
            }
        }
    }

    if (regionStatus === 'available') {
        const fallback = statusesByProductCity?.[`::${city}`];
        if (fallback) regionStatus = fallback;
    }

    return regionStatus;
};

/**
 * Groups a flat list of region items by `city`, so callers can collapse
 * same-city-but-different-`compute_location` regions (e.g. two distinct
 * "Chicago" entries) into a single collapsible row instead of rendering
 * duplicate, indistinguishable city labels side by side.
 *
 * Preserves the original relative ordering of first-seen cities. Single-item
 * cities pass through as `{ isGroup: false, item }` so existing single-row
 * rendering is untouched; cities with 2+ items become
 * `{ isGroup: true, city, items }`.
 */
export const groupItemsByCity = (items = []) => {
    const byCity = new Map();

    items.forEach(item => {
        const city = item?.city || '';
        if (!byCity.has(city)) byCity.set(city, []);
        byCity.get(city).push(item);
    });

    return Array.from(byCity.values()).map(group => (
        group.length > 1
            ? { isGroup: true, city: group[0].city, items: group }
            : { isGroup: false, item: group[0] }
    ));
};
